import { PRICING_TREATMENTS, type PricingTreatment } from '@/lib/sales/line-economics';
import type { ServiceComponent } from '@/types/pricebook';

/**
 * What the pricebook form posts for a service's "Included parts".
 *
 * The API validates this list with `whitelist` + `forbidNonWhitelisted`, so the
 * payload is not forgiving: a key no DTO declares, or a value outside a
 * declared union, fails the WHOLE save — and the operator is shown a toast
 * naming a storage field they have never seen ("Fix the Pricing Treatment
 * field"). Two separate ways to hit that, both closed here:
 *
 *   an out-of-union value  `@IsOptional()` skips `null` and `undefined` but NOT
 *                          `''`. Anything that is not one of the two treatments
 *                          is dropped rather than posted.
 *   a key with nothing in  "not decided" is expressed by OMITTING the key, not
 *                          by sending `null`. Omitted is legal, means exactly
 *                          "nobody has decided" (the catalog item's
 *                          `chargeable` answers), and is the only spelling of
 *                          it every version of the API has understood.
 *
 * Built explicitly, field by field, rather than by spreading the editor's row:
 * a spread posts whatever the row happens to be carrying, which is how a field
 * the server does not know about reaches it in the first place.
 */
export interface ServiceComponentPayload {
  itemId: string;
  quantity: number;
  displayOrder: number;
  isOptional: boolean;
  pricingMethod?: 'CATALOG_PRICE' | 'COST_PLUS';
  markupProfileId?: string | null;
  customMarkupPercent?: number | null;
  showInDetail?: boolean;
  pricingTreatment?: PricingTreatment;
}

/** The value the API accepts, or nothing at all. Never `''`, never a guess. */
export function readPricingTreatment(value: unknown): PricingTreatment | undefined {
  return PRICING_TREATMENTS.includes(value as PricingTreatment)
    ? (value as PricingTreatment)
    : undefined;
}

export function toServiceComponentPayload(
  component: ServiceComponent,
  displayOrder: number,
): ServiceComponentPayload {
  const treatment = readPricingTreatment(component.pricingTreatment);
  return {
    itemId: component.itemId,
    quantity: component.quantity,
    displayOrder,
    // Persisted on the relationship, NOT estimate-only state: it decides
    // whether the part is offered-but-unselected when the service lands on an
    // estimate. Forward what was loaded — hard-coding `false` rewrote every
    // optional add-on to required the next time anything else on the service
    // was saved.
    isOptional: component.isOptional ?? false,
    ...(component.pricingMethod ? { pricingMethod: component.pricingMethod } : {}),
    ...(component.markupProfileId !== undefined
      ? { markupProfileId: component.markupProfileId }
      : {}),
    ...(component.customMarkupPercent !== undefined
      ? { customMarkupPercent: component.customMarkupPercent }
      : {}),
    ...(typeof component.showInDetail === 'boolean'
      ? { showInDetail: component.showInDetail }
      : {}),
    ...(treatment ? { pricingTreatment: treatment } : {}),
  };
}

/** The whole list, in the order the operator arranged it. */
export function toServiceComponentPayloads(
  components: ServiceComponent[],
  allowOptional = true,
): ServiceComponentPayload[] {
  return components
    .filter((component) => component.itemId)
    .map((component, index) =>
      toServiceComponentPayload(
        allowOptional ? component : { ...component, isOptional: false },
        index,
      ),
    );
}
